import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, Store, Package, ShoppingBag, Tags, Ticket, Settings,
  IndianRupee, Trash2, Plus, Pencil, Ban, CheckCircle2, FileCheck2, PackageSearch, Banknote, LayoutTemplate, ScrollText, Percent,
} from "lucide-react";
import { RequestsPanel, VendorsPanel, PayoutsPanel } from "@/pages/admin/SellerPanels";
import { ModerationPanel, CmsPanel } from "@/pages/admin/CatalogPanels";
import { MarketOverview, AuditPanel, MarketSettingsPanel } from "@/pages/admin/OpsPanels";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/constants/branding";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export function AdminPanels({ isSuper }) {
  const { user } = useAuth();
  const { loadPublic } = useStore();
  const [mstats, setMstats] = useState(null);
  const NAV = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "requests", label: "Seller Requests", icon: FileCheck2, badge: mstats?.pending_applications || 0 },
    { key: "vendors", label: "Vendors", icon: Store },
    { key: "moderation", label: "Product Review", icon: PackageSearch, badge: mstats?.pending_products || 0 },
    { key: "products", label: "All Products", icon: Package },
    { key: "orders", label: "Orders", icon: ShoppingBag },
    { key: "payouts", label: "Payouts", icon: Banknote },
    { key: "users", label: "Users", icon: Users },
    { key: "categories", label: "Categories", icon: Tags },
    { key: "coupons", label: "Coupons", icon: Ticket },
    { key: "cms", label: "Content (CMS)", icon: LayoutTemplate },
    { key: "marketplace", label: "Marketplace Rules", icon: Percent },
    ...(isSuper ? [{ key: "audit", label: "Audit Log", icon: ScrollText }] : []),
    { key: "settings", label: isSuper ? "Permissions & Visibility" : "Panel Visibility", icon: Settings },
  ];

  const [active, setActive] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t || "overview";
  });
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [pv, setPv] = useState({ vendor: true, admin: true, super_admin: true });

  const [catDialog, setCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", image: "" });
  const [catEdit, setCatEdit] = useState(null);
  const [coupDialog, setCoupDialog] = useState(false);
  const [coupForm, setCoupForm] = useState({ code: "", type: "percent", value: "", min_order: "", max_discount: "", active: true });
  const [coupEdit, setCoupEdit] = useState(null);

  const loadAll = async () => {
    const [s, u, p, o, c, cp, st, ms] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/products"),
      api.get("/admin/orders"), api.get("/categories"), api.get("/admin/coupons"), api.get("/admin/settings"),
      api.get("/admin/marketplace-stats"),
    ]);
    setStats(s.data); setUsers(u.data); setProducts(p.data); setOrders(o.data);
    setCategories(c.data); setCoupons(cp.data); setPv(st.data.panel_visibility); setMstats(ms.data);
  };
  const refreshBadges = () => api.get("/admin/marketplace-stats").then((r) => setMstats(r.data)).catch(() => {});

  useEffect(() => { loadAll().catch((e) => toast.error(apiError(e))); }, []);

  const toggleActive = async (u) => {
    try { await api.put(`/admin/users/${u.id}/active`, { active: !u.active }); loadAll(); toast.success("User updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); loadAll(); toast.success("Role updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try { await api.delete(`/admin/users/${id}`); loadAll(); toast.success("User deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); loadAll(); toast.success("Product deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setOrderStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); loadAll(); toast.success("Order updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (catEdit) await api.put(`/admin/categories/${catEdit}`, catForm);
      else await api.post("/admin/categories", catForm);
      toast.success("Category saved"); setCatDialog(false); loadAll();
    } catch (err) { toast.error(apiError(err)); }
  };
  const deleteCategory = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCoupon = async (e) => {
    e.preventDefault();
    const payload = {
      code: coupForm.code, type: coupForm.type, value: parseFloat(coupForm.value) || 0,
      min_order: parseFloat(coupForm.min_order) || 0,
      max_discount: coupForm.max_discount ? parseFloat(coupForm.max_discount) : null,
      active: coupForm.active,
    };
    try {
      if (coupEdit) await api.put(`/admin/coupons/${coupEdit}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success("Coupon saved"); setCoupDialog(false); loadAll();
    } catch (err) { toast.error(apiError(err)); }
  };
  const deleteCoupon = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); toast.success("Coupon deleted"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const updatePv = async (key, val) => {
    try {
      const { data } = await api.put("/admin/settings/panel-visibility", { [key]: val });
      setPv(data.panel_visibility);
      loadPublic();
      toast.success("Visibility updated");
    } catch (e) { toast.error(apiError(e)); }
  };


  return (
    <DashboardShell title={isSuper ? "Super Admin" : "Admin"} subtitle={isSuper ? "Super Admin Dashboard" : "Admin Dashboard"} nav={NAV} active={active} onNavigate={setActive}>
      {active === "overview" && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard label="Users" value={stats.users} icon={Users} />
          <StatCard label="Vendors" value={stats.vendors} icon={Store} />
          <StatCard label="Products" value={stats.products} icon={Package} />
          <StatCard label="Orders" value={stats.orders} icon={ShoppingBag} />
          <StatCard label="Revenue" value={formatINR(stats.revenue)} icon={IndianRupee} />
        </div>
      )}
      {active === "overview" && <div className="mt-6"><MarketOverview stats={mstats} onNavigate={setActive} /></div>}

      {active === "users" && (
        <Table
          head={["Name", "Email", "Role", "Status", "Actions"]}
          rows={users.map((u) => [
            u.name,
            <span className="text-muted-foreground">{u.email}</span>,
            isSuper && u.role !== "super_admin" ? (
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                <SelectTrigger className="h-8 w-32" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            ) : <span className="capitalize">{u.role.replace("_", " ")}</span>,
            <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{u.active ? "Active" : "Disabled"}</span>,
            u.role === "super_admin" ? <span className="text-xs text-muted-foreground">Protected</span> : (
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>
                  {u.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
                </Button>
                {isSuper && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteUser(u.id)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ),
          ])}
          testid="admin-users-table"
        />
      )}

      {active === "requests" && <RequestsPanel isSuper={isSuper} onChanged={refreshBadges} />}
      {active === "vendors" && <VendorsPanel isSuper={isSuper} onChanged={refreshBadges} />}
      {active === "moderation" && <ModerationPanel onChanged={refreshBadges} />}
      {active === "payouts" && <PayoutsPanel isSuper={isSuper} onChanged={refreshBadges} />}
      {active === "cms" && <CmsPanel />}
      {active === "marketplace" && <MarketSettingsPanel isSuper={isSuper} />}
      {active === "audit" && isSuper && <AuditPanel />}

      {active === "products" && (
        <Table
          head={["Product", "Category", "Price", "Stock", "Vendor", "Actions"]}
          rows={products.map((p) => [
            <div className="flex items-center gap-2"><img src={(p.images || [])[0]} alt="" className="h-8 w-8 rounded object-cover" /><span className="line-clamp-1 max-w-[160px]">{p.name}</span></div>,
            p.category, formatINR(p.price), p.stock, <span className="text-muted-foreground text-xs">{p.vendor_name}</span>,
            <div className="flex justify-end"><Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteProduct(p.id)} data-testid={`admin-product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button></div>,
          ])}
          testid="admin-products-table"
        />
      )}

      {active === "orders" && (
        <Table
          head={["Order", "Customer", "Total", "Payment", "Status"]}
          empty="No orders yet."
          rows={orders.map((o) => [
            <span className="font-medium">{o.order_number}</span>, o.customer_name, formatINR(o.total), o.payment_method.toUpperCase(),
            <Select value={o.status} onValueChange={(v) => setOrderStatus(o.id, v)}>
              <SelectTrigger className="h-8 w-32" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>,
          ])}
          testid="admin-orders-table"
        />
      )}

      {active === "categories" && (
        <div>
          <div className="flex justify-between mb-4"><h2 className="font-semibold text-lg">Categories</h2><Button className="gap-1.5" onClick={() => { setCatForm({ name: "", image: "" }); setCatEdit(null); setCatDialog(true); }} data-testid="admin-add-category"><Plus className="h-4 w-4" /> Add</Button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-border p-4 flex items-center gap-3" data-testid={`admin-category-${c.id}`}>
                <img src={c.image} alt="" className="h-12 w-12 rounded object-cover bg-muted" />
                <div className="flex-1"><p className="font-semibold">{c.name}</p><p className="text-xs text-muted-foreground">{c.product_count} products</p></div>
                <Button size="sm" variant="ghost" onClick={() => { setCatForm({ name: c.name, image: c.image }); setCatEdit(c.id); setCatDialog(true); }}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCategory(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {active === "coupons" && (
        <div>
          <div className="flex justify-between mb-4"><h2 className="font-semibold text-lg">Coupons</h2><Button className="gap-1.5" onClick={() => { setCoupForm({ code: "", type: "percent", value: "", min_order: "", max_discount: "", active: true }); setCoupEdit(null); setCoupDialog(true); }} data-testid="admin-add-coupon"><Plus className="h-4 w-4" /> Add</Button></div>
          <Table
            head={["Code", "Type", "Value", "Min Order", "Status", "Actions"]}
            empty="No coupons yet."
            rows={coupons.map((c) => [
              <span className="font-mono font-semibold">{c.code}</span>, c.type, c.type === "percent" ? `${c.value}%` : formatINR(c.value), formatINR(c.min_order),
              <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span>,
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setCoupForm({ code: c.code, type: c.type, value: String(c.value), min_order: String(c.min_order), max_discount: c.max_discount ? String(c.max_discount) : "", active: c.active }); setCoupEdit(c.id); setCoupDialog(true); }}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCoupon(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>,
            ])}
            testid="admin-coupons-table"
          />
        </div>
      )}

      {active === "settings" && (
        <div className="max-w-xl space-y-4">
          <h2 className="font-semibold text-lg">Panel Visibility</h2>
          <p className="text-sm text-muted-foreground">Control which panels are visible in navigation. Hiding a panel never deletes any data — re-enable it anytime.</p>
          <div className="bg-white rounded-xl border border-border divide-y">
            <PvRow label="Vendor Panel" desc="Show vendor dashboard access" checked={pv.vendor} onChange={(v) => updatePv("vendor", v)} testid="pv-vendor" />
            {isSuper && <PvRow label="Admin Panel" desc="Show admin dashboard access" checked={pv.admin} onChange={(v) => updatePv("admin", v)} testid="pv-admin" />}
            {isSuper && <PvRow label="Super Admin Panel" desc="Show super admin dashboard access" checked={pv.super_admin} onChange={(v) => updatePv("super_admin", v)} testid="pv-super" />}
          </div>
          {!isSuper && <p className="text-xs text-muted-foreground">Admin and Super Admin panel visibility can only be changed by a Super Admin.</p>}
        </div>
      )}

      {/* Category dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{catEdit ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <form onSubmit={saveCategory} className="space-y-3">
            <div><Label>Name</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} required data-testid="category-name" /></div>
            <div><Label>Image URL</Label><Input value={catForm.image} onChange={(e) => setCatForm({ ...catForm, image: e.target.value })} data-testid="category-image" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setCatDialog(false)}>Cancel</Button><Button type="submit" data-testid="category-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Coupon dialog */}
      <Dialog open={coupDialog} onOpenChange={setCoupDialog}>
        <DialogContent data-testid="coupon-dialog">
          <DialogHeader><DialogTitle>{coupEdit ? "Edit Coupon" : "Add Coupon"}</DialogTitle></DialogHeader>
          <form onSubmit={saveCoupon} className="space-y-3">
            <div><Label>Code</Label><Input value={coupForm.code} onChange={(e) => setCoupForm({ ...coupForm, code: e.target.value.toUpperCase() })} required data-testid="coupon-code" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={coupForm.type} onValueChange={(v) => setCoupForm({ ...coupForm, type: v })}>
                  <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Value</Label><Input type="number" value={coupForm.value} onChange={(e) => setCoupForm({ ...coupForm, value: e.target.value })} required data-testid="coupon-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min Order (₹)</Label><Input type="number" value={coupForm.min_order} onChange={(e) => setCoupForm({ ...coupForm, min_order: e.target.value })} /></div>
              <div><Label>Max Discount (₹)</Label><Input type="number" value={coupForm.max_discount} onChange={(e) => setCoupForm({ ...coupForm, max_discount: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={coupForm.active} onCheckedChange={(v) => setCoupForm({ ...coupForm, active: v })} /> Active</label>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setCoupDialog(false)}>Cancel</Button><Button type="submit" data-testid="coupon-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function PvRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between p-4">
      <div><p className="font-medium">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function Table({ head, rows, testid, empty = "No records." }) {
  return (
    <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid={testid}>
      {rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-muted-foreground">
            <tr>{head.map((h, i) => <th key={i} className={`p-3 font-medium ${i === head.length - 1 ? "text-right" : ""}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                {r.map((c, j) => <td key={j} className={`p-3 ${j === r.length - 1 ? "text-right" : ""}`}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  return <AdminPanels isSuper={false} />;
}
